import type { Matrix3, Point } from '../contract/types.js';

export const IDENTITY: Matrix3 = [1, 0, 0, 1, 0, 0];

/** 1 PostScript point = 25.4/72 millimetres. */
export const PT_TO_MM = 25.4 / 72;

/** Returns the matrix equivalent to applying `b` first, then `a`. */
export function multiply(a: Matrix3, b: Matrix3): Matrix3 {
  const [a0, a1, a2, a3, a4, a5] = a;
  const [b0, b1, b2, b3, b4, b5] = b;
  return [
    a0 * b0 + a2 * b1,
    a1 * b0 + a3 * b1,
    a0 * b2 + a2 * b3,
    a1 * b2 + a3 * b3,
    a0 * b4 + a2 * b5 + a4,
    a1 * b4 + a3 * b5 + a5,
  ];
}

export function applyTo(m: Matrix3, p: Point): Point {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

export function invert(m: Matrix3): Matrix3 {
  const det = m[0] * m[3] - m[1] * m[2];
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) {
    throw new Error('singular matrix cannot be inverted');
  }
  const [a, b, c, d, e, f] = m;
  return [
    d / det,
    -b / det,
    -c / det,
    a / det,
    (c * f - d * e) / det,
    (b * e - a * f) / det,
  ];
}

/**
 * PDF space (points, origin top-left, y downward) to drawing space
 * (millimetres, origin bottom-left, y upward).
 *
 * The flip is applied to coordinates here rather than expressed as a mirror
 * transform later. Reflection is not representable in a right-handed object
 * coordinate system, so a DXF library asked to mirror preserves world geometry
 * by flipping the extrusion vector to (0,0,-1) and negating raw coordinates.
 * World endpoints stay correct, but every consumer reading the raw centre or
 * angle attributes gets nonsense — and a structural audit never flags it,
 * because (0,0,-1) is perfectly legal.
 */
export function buildPdfToDrawing(pageHeightPt: number, mmPerPoint: number): Matrix3 {
  return [mmPerPoint, 0, 0, -mmPerPoint, 0, pageHeightPt * mmPerPoint];
}

/**
 * Arc angles under a vertical flip negate AND swap.
 *
 * Reflection reverses orientation, while DXF arcs are always counter-clockwise
 * about the extrusion vector. So for y' = H - y:
 *
 *   newStart = (-oldEnd)   mod 360
 *   newEnd   = (-oldStart) mod 360
 *
 * Test this with an asymmetric arc. A symmetric one (45 to 135 degrees) passes
 * even when start and end are wrongly swapped.
 */
export function transformAngleUnderFlip(
  startDeg: number,
  endDeg: number,
): { startAngle: number; endAngle: number } {
  return { startAngle: normaliseDegrees(-endDeg), endAngle: normaliseDegrees(-startDeg) };
}

/** Polyline bulge signs also negate under the flip. */
export function transformBulgeUnderFlip(bulge: number): number {
  return -bulge;
}

export function normaliseDegrees(a: number): number {
  const r = a % 360;
  // The `+ 0` turns a negative zero into a positive one, so a rotation of zero
  // serialises as 0 rather than -0 and compares equal to it.
  return r < 0 ? r + 360 : r + 0;
}
